import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { LogOut, Settings as SettingsIcon, Image as ImageIcon, Star, Clock, Calendar, Type, ExternalLink, Gem, Sparkles, Sparkle, Camera, Stethoscope } from 'lucide-react';
import TextsEditor from './admin/TextsEditor';
import PiercingsAdmin from './admin/PiercingsAdmin';
import GalleryAdmin from './admin/GalleryAdmin';
import BijouxAdmin from './admin/BijouxAdmin';
import GrillzAdmin from './admin/GrillzAdmin';
import StrassAdmin from './admin/StrassAdmin';
import RealisationsAdmin from './admin/RealisationsAdmin';
import ProthesesAdmin from './admin/ProthesesAdmin';
import ReviewsAdmin from './admin/ReviewsAdmin';
import HoursAdmin from './admin/HoursAdmin';
import BookingsAdmin from './admin/BookingsAdmin';

const TABS = [
  { id: 'texts', label: 'Textes', icon: Type },
  { id: 'piercings', label: 'Piercings', icon: SettingsIcon },
  { id: 'bijoux', label: 'Bijoux', icon: Gem },
  { id: 'grillz', label: 'Grillz', icon: Sparkles },
  { id: 'strass', label: 'Strass dentaire', icon: Sparkle },
  { id: 'protheses', label: 'Prothèses', icon: Stethoscope },
  { id: 'realisations', label: 'Réalisations', icon: Camera },
  { id: 'gallery', label: 'Galerie tatouage', icon: ImageIcon },
  { id: 'reviews', label: 'Avis', icon: Star },
  { id: 'hours', label: 'Horaires', icon: Clock },
  { id: 'bookings', label: 'Réservations', icon: Calendar },
];

const Admin = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('texts');
  const [content, setContent] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate('/admin/login');
    if (!loading && user && !user.is_admin) navigate('/admin/denied');
  }, [user, loading, navigate]);

  const refresh = useCallback(async () => {
    const r = await api.get('/content');
    setContent(r.data);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading || !user || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F4]">
        <div className="display text-[24px] text-[#957340]">Chargement…</div>
      </div>
    );
  }

  const onLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#FAF8F4]">
      {/* Top bar */}
      <header className="bg-white border-b border-[#E8E2D5] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border border-[#1A1A1A] flex items-center justify-center">
              <span className="display text-[15px] leading-none">D</span>
            </div>
            <div>
              <div className="display text-[16px] tracking-wide">DEVIL TATTOOZ</div>
              <div className="text-[9px] tracking-[0.32em] uppercase text-[#8A8275]">Administration</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A] hover:text-[#957340] flex items-center gap-2">
              Voir le site <ExternalLink size={12} />
            </a>
            <div className="hidden md:flex items-center gap-3 border-l border-[#E8E2D5] pl-4">
              {user.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
              <div className="text-[12px] text-[#3A3A3A]">{user.email}</div>
            </div>
            <button onClick={onLogout} className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase border border-[#1A1A1A] px-3 py-2 hover:bg-[#1A1A1A] hover:text-white transition-colors">
              <LogOut size={12} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-3">
            <div className="bg-white border border-[#E8E2D5] p-2 sticky top-24">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-[13px] tracking-wide transition-colors ${active ? 'bg-[#1A1A1A] text-white' : 'text-[#1A1A1A] hover:bg-[#FAF8F4]'}`}
                  >
                    <Icon size={15} className={active ? 'text-[#D4B987]' : 'text-[#957340]'} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-9">
            <div className="bg-white border border-[#E8E2D5] p-6 md:p-10">
              {tab === 'texts' && <TextsEditor content={content} refresh={refresh} />}
              {tab === 'piercings' && <PiercingsAdmin content={content} refresh={refresh} />}
              {tab === 'bijoux' && <BijouxAdmin content={content} refresh={refresh} />}
              {tab === 'grillz' && <GrillzAdmin content={content} refresh={refresh} />}
              {tab === 'strass' && <StrassAdmin content={content} refresh={refresh} />}
              {tab === 'protheses' && <ProthesesAdmin content={content} refresh={refresh} />}
              {tab === 'realisations' && <RealisationsAdmin content={content} refresh={refresh} />}
              {tab === 'gallery' && <GalleryAdmin content={content} refresh={refresh} />}
              {tab === 'reviews' && <ReviewsAdmin content={content} refresh={refresh} />}
              {tab === 'hours' && <HoursAdmin content={content} refresh={refresh} />}
              {tab === 'bookings' && <BookingsAdmin />}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Admin;
